// @/utils/pdfQuotationBase.js

/**
 * PDFQuotationBase
 * รวมเมธอด/ฟังก์ชันที่ใช้ร่วมกันระหว่าง Fixed Price Quotation และ Fee Estimate
 * (header บริษัท, ข้อมูลลูกค้า, กรอบตารางแบบ absolutePosition ฯลฯ)
 *
 * ตัว generator เฉพาะแต่ละประเภทเอกสาร:
 *   - pdfQuotationFixedGenerator.js    (doc_type === "fixed")
 *   - pdfQuotationEstimateGenerator.js (doc_type === "estimate")
 * จะ extends คลาสนี้ แล้วเติมเฉพาะส่วนที่ต่างกัน (โครงตาราง/แถวสรุปยอด/เงื่อนไขท้ายเอกสาร)
 */
export default class PDFQuotationBase {
    /**
     * รอให้ pdfMake พร้อมใช้งาน (โหลดฟอนต์ภาษาไทยเสร็จ)
     * @returns {Promise<boolean>}
     */
    waitForPdfMakeReady() {
        return new Promise((resolve, reject) => {
            const checkInterval = setInterval(() => {
                if (typeof pdfMake !== "undefined" && pdfMake.vfs && pdfMake.fonts) {
                    clearInterval(checkInterval);
                    resolve(true);
                }
            }, 100);

            setTimeout(() => {
                clearInterval(checkInterval);
                reject(new Error("pdfMake โหลดไม่สำเร็จ"));
            }, 10000);
        });
    }
    parseAuthorizedFeeLimit(value) {
        if (value === null || value === undefined || value === "") return null;
        const num = Number(String(value).replace(/,/g, "").trim());
        if (isNaN(num) || num <= 0) return null;
        return num;
    }
    formatNumber(value) {
        if (!value && value !== 0) return "0.00";
        return new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    }

    formatDate(dateString) {
        if (!dateString) return "-";
        if (dateString.includes("/")) return dateString;

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;

        const day = date.getDate().toString().padStart(2, "0");
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    }

    /**
     * เติมค่า default ของบริษัทให้ header
     */
    buildCompanyHeader(headerInput) {
        const header = { ...(headerInput || {}) };

        if (["D", "L"].includes(header.company_inv)) {
            header.com_eng_1 = header.com_eng_1 || "DEJ-UDOM & ASSOCIATES LTD.";
            header.com_eng_2 = header.com_eng_2 || "ATTORNEYS AT LAW";
            header.com_thai = header.com_thai || "บริษัท เดชอุดม แอนด์ แอสโซซิเอทส์ จำกัด";
            header.com_add_name = header.com_add_name || "CHARN ISSARA TOWER 1, 2nd FLOOR,";
            header.com_add_no = header.com_add_no || "942/69 RAMA IV ROAD,";
            header.com_add_city = header.com_add_city || "KWAENG SURIYAWONG, KHET BANGRAK,";
            header.com_add_country = header.com_add_country || "BANGKOK 10500, THAILAND";
            header.com_add_tel = header.com_add_tel || "Tel : 0-2233-0055, 0-2267-5152";
            header.com_add_email = header.com_add_email || "E-MAIL : account@dejudom.com";
            header.com_add_tax_id = header.com_add_tax_id || 'TAX ID. No. 0105534015348/"HEAD OFFICE"';
        } else {
            header.com_eng_1 = header.com_name || "ไม่มีข้อมูลบริษัท";
            header.com_eng_2 = header.com_eng_2 || "";
            header.com_thai = header.inv_company_name_th || "ไม่มีข้อมูลบริษัท";
            header.com_add_name = header.inv_company_address ? `${header.inv_company_address},` : " ";
            header.com_add_no =
                header.inv_company_address2 && header.inv_company_address2 !== ""
                    ? `${header.inv_company_address2},`
                    : " ";
            header.com_add_city = `${header.inv_subdistrict || ""}, ${header.inv_district || ""},`;
            header.com_add_country = `${header.inv_province || ""}, ${header.inv_postcode || ""}, ${header.inv_country || ""
                }`;
            header.com_add_tel = `Tel : ${header.inv_company_tel || "-"}`;
            header.com_add_email = `E-MAIL : ${header.inv_company_email || "-"}`;
            header.com_add_tax_id = `TAX ID. No. ${header.inv_tax_id || "-"}/"${header.inv_company_branch || "-"}"`;
        }

        return header;
    }

    generateCompanyHeaderBlock(header) {
        return {
            columns: [
                {
                    width: "50%",
                    stack: [
                        { text: header.com_eng_1, fontSize: 16, bold: true, margin: [0, 0, 0, 1] },
                        { text: header.com_eng_2, fontSize: 9, margin: [0, 0, 0, 2] },
                        { text: header.com_thai, fontSize: 11, margin: [0, 0, 0, 1] },
                    ],
                },
                {
                    width: "50%",
                    stack: [
                        { text: header.com_add_name, fontSize: 8, alignment: "right" },
                        { text: header.com_add_no, fontSize: 8, alignment: "right" },
                        { text: header.com_add_city, fontSize: 8, alignment: "right" },
                        { text: header.com_add_country, fontSize: 8, alignment: "right" },
                        { text: header.com_add_tel, fontSize: 8, alignment: "right", margin: [0, 2, 0, 0] },
                        { text: header.com_add_email, fontSize: 8, alignment: "right" },
                        { text: header.com_add_tax_id, fontSize: 8, alignment: "right", bold: true },
                    ],
                },
            ],
            margin: [0, 0, 0, 15],
        };
    }

    generateTitleBlock(header, docTitle) {
        return {
            columns: [
                { width: "60%", text: docTitle, fontSize: 18, bold: true },
                {
                    width: "40%",
                    text: `${header.doc_title_en || "Quotation No"} / ${header.doc_title_th || "เลขที่ใบเสนอราคา"}: ${header.doc_no || ""
                        }`,
                    fontSize: 10,
                    alignment: "right",
                    margin: [0, 6, 0, 0],
                },
            ],
            margin: [0, 0, 0, 5],
        };
    }

    generateDividerLine() {
        return {
            canvas: [{ type: "line", x1: 0, y1: 0, x2: 545, y2: 0, lineWidth: 1 }],
            margin: [0, 0, 0, 10],
        };
    }

    /**
     * ประเมินความสูงจริงของ generateClientInfoBlock ตามจำนวนบรรทัดที่อยู่ลูกค้าจริง
     */
    estimateClientInfoHeight(header) {
        const addressLines = Array.isArray(header.customer_address)
            ? header.customer_address
            : (header.customer_address || "").split("\n");
        const addressLineCount = Math.max(addressLines.length, 1);

        const fixedPart = 16 + 16 + 13 + 12;
        const addressPart = addressLineCount * 13 + 6;

        // คอลัมน์ขวา: Date (13+4) + Credit Term (13) + VAT Rate (4+13 ถ้ามี) + Authorized Fee Limit (4+13 ถ้ามี)
        // ปกติคอลัมน์ซ้าย (4 บรรทัด) สูงกว่าอยู่แล้ว แต่เผื่อไว้กรณี address สั้นมาก (1 บรรทัด)
        const hasVatRate = header.vat_percent !== undefined && header.vat_percent !== null && header.vat_percent !== "";
        const hasCap = this.parseAuthorizedFeeLimit(header.not_to_exceed_cap) !== null;
        const rightColumnHeight = 13 + 4 + 13 + (hasVatRate ? 4 + 13 : 0) + (hasCap ? 4 + 13 : 0);

        return Math.max(fixedPart + addressPart, rightColumnHeight + 16);
    }
    generateClientInfoBlock(header) {
        const addressLines = Array.isArray(header.customer_address)
            ? header.customer_address
            : (header.customer_address || "").split("\n");

        // ===== คอลัมน์ขวา: Date / Credit Term / (Authorized Fee Limit ถ้ามี) =====
        // บรรทัดที่ 3 จะไปเรียงระดับเดียวกับ "Tax ID" ของคอลัมน์ซ้ายพอดี
        // margin [0, 3, 0, 2] และ fontSize 9 ตรงกับบรรทัด Tax ID เป๊ะ
        const rightStack = [
            {
                text: `Date / วันที่: ${this.formatDate(header.doc_date)}`,
                fontSize: 9,
                alignment: "right",
                margin: [0, 0, 0, 4],
            },
            {
                text: `Credit Term / กำหนดชำระ: ${header.payment_term || "0"} Days`,
                fontSize: 9,
                alignment: "right",
            },
        ];

        // ===== VAT Rate — แสดงเมื่อมีการระบุอัตรา VAT มาในเอกสาร =====
        if (header.vat_percent !== undefined && header.vat_percent !== null && header.vat_percent !== "") {
            rightStack.push({
                text: `VAT Rate / อัตรา VAT: ${this.formatNumber(header.vat_percent)}%`,
                fontSize: 9,
                alignment: "right",
                margin: [0, 4, 0, 0],
            });
        }

        const cap = this.parseAuthorizedFeeLimit(header.not_to_exceed_cap);
        if (cap !== null) {
            rightStack.push({
                text: `Authorized Fee Limit / วงเงินที่ได้รับอนุมัติ : ${header.currency || "THB"} ${this.formatNumber(cap)}`,
                fontSize: 9,
                alignment: "right",
                margin: [0, 4, 0, 0],
            });
        }

        return {
            columns: [
                {
                    width: "58%",
                    stack: [
                        {
                            text: [
                                { text: "To / ถึง: ", fontSize: 10 },
                                { text: header.customer_name || "", fontSize: 10, bold: true },
                            ],
                            margin: [0, 0, 0, 2],
                        },
                        { text: addressLines, fontSize: 9, margin: [0, 0, 0, 5] },
                        { text: `Tax ID: ${header.customer_taxid || "-"}`, fontSize: 9, margin: [0, 3, 0, 2] },
                        { text: `Attention : ${header.attention || header.customer_contact_name || "-"}`, fontSize: 9 },
                    ],
                },
                {
                    width: "42%",
                    stack: rightStack,
                },
            ],
            margin: [0, 0, 0, 10],
        };
    }
    /**
     * แปลง items ให้เป็น table body ของ pdfMake — ใช้ร่วมกันทั้ง 2 ประเภทเอกสาร
     * Fixed Price: item.amount (ตัวเลข) -> โชว์เป็นจำนวนเงินจัดรูปแบบ
     * Fee Estimate: item.rate (string เช่น "30,000.00 - 50,000.00") -> โชว์ตรงๆ
     */
    buildItemRows(items) {
        return (items || []).map((item) => {
            // ===== ชื่อรายการ + หมายเหตุ "ไม่คิด VAT" ต่อท้ายในบรรทัดเดียวกัน =====
            // แสดงเฉพาะเมื่อส่ง apply_vat: false มาอย่างชัดเจน (undefined = ไม่ระบุ ถือว่าคิด VAT ตามปกติ)
            const nameRuns = [{ text: item.description || item.name || "", fontSize: 9, bold: !!item.bold }];
            if (item.apply_vat === false) {
                nameRuns.push({
                    text: " (Not subject to VAT / ไม่คิด VAT)",
                    fontSize: 8,
                    italics: true,
                    bold: false,
                });
            }

            const descStack = [{ text: nameRuns }];
            if (item.detail) {
                descStack.push({
                    text: item.detail,
                    fontSize: 8,
                    italics: true,
                    color: item.bold ? "#000000" : "#1565C0",
                    margin: [0, 2, 0, 0],
                });
            }

            const amountText =
                item.amount !== undefined && item.amount !== null ? this.formatNumber(item.amount) : item.rate || "";

            return [
                { stack: descStack, margin: [5, 4, 5, 4] },
                { text: amountText, fontSize: 9, bold: !!item.bold, alignment: "right", margin: [5, 4, 5, 4] },
            ];
        });
    }

    /**
     * ความสูงต่อแถวของ buildItemRows โดยนับจำนวนบรรทัดของ detail ด้วย
     */
    /**
        * ประมาณจำนวนบรรทัดจริงหลัง word wrap — นับทั้ง \n ที่ผู้ใช้กดเอง และบรรทัดที่
        * pdfMake ตัดเองเมื่อข้อความยาวเกินความกว้างคอลัมน์
        *
        * ⚠️ ตัวหารเป็นค่าประมาณความกว้างเฉลี่ยต่ออักขระของฟอนต์ Sarabun (≈ 0.5 เท่าของ
        * fontSize) จงใจตั้งให้ "แคบกว่าจริงเล็กน้อย" เพื่อให้ประมาณได้จำนวนบรรทัดมากกว่า
        * ความจริง — เผื่อพื้นที่เกินไว้ดีกว่าเผื่อขาด เพราะถ้าประมาณต่ำไป เนื้อหาจะล้น
        * เกินพื้นที่ที่กะไว้ แล้วดันบล็อกท้ายเอกสาร (Terms) ไปหน้าใหม่โดยไม่ตั้งใจ
        */
    estimateLineCount(text, availableWidth, fontSize) {
        const charsPerLine = Math.max(Math.floor(availableWidth / (fontSize * 0.5)), 1);

        // ⚠️ ตัดสระบน/สระล่าง/วรรณยุกต์/ทัณฑฆาต ของไทยออกก่อนนับ (U+0E31, U+0E34–U+0E3A,
        // U+0E47–U+0E4E) เพราะอักขระกลุ่มนี้ซ้อนอยู่บน/ล่างพยัญชนะ ไม่กินความกว้างบรรทัด
        // ถ้านับรวมด้วย ข้อความไทยจะถูกประเมินว่ายาวกว่าจริงมาก (บางประโยคเกินจริงถึง 30%)
        const visibleLength = (line) => line.replace(/[\u0E31\u0E34-\u0E3A\u0E47-\u0E4E]/g, "").trim().length;

        return String(text === null || text === undefined ? "" : text)
            .replace(/\r\n/g, "\n")
            .split("\n")
            .reduce((sum, line) => sum + Math.max(Math.ceil(visibleLength(line) / charsPerLine), 1), 0);
    }

    /**
     * ความสูงต่อแถวของ buildItemRows — นับบรรทัดจริงหลัง word wrap ทั้งชื่อรายการ
     * (fontSize 9) และรายละเอียด (fontSize 8)
     * @param {Array} items
     * @param {number} colWidth ความกว้างคอลัมน์ Description ของเอกสารนั้น (Fixed 423 / Estimate 340)
     */
    buildRowHeights(items, colWidth = 423) {
        const avail = colWidth - 10; // หัก margin ซ้าย/ขวาของ cell ([5, x, 5, x])

        return (items || []).map((item) => {
            // หมายเหตุ "ไม่คิด VAT" ต่อท้ายชื่อรายการในบรรทัดเดียวกัน จึงนับความยาวรวมไปด้วย
            // เพื่อให้ประเมินจำนวนบรรทัดหลัง word wrap ได้ถูกต้อง
            const nameText =
                (item.description || item.name || "") +
                (item.apply_vat === false ? " (Not subject to VAT / ไม่คิด VAT)" : "");

            const nameLines = this.estimateLineCount(nameText, avail, 9);
            let height = Math.max(8 + nameLines * 12, 22);

            if (item.detail) {
                const detailLines = this.estimateLineCount(item.detail, avail, 8);
                height += 2 + detailLines * 11;
            }

            return height;
        });
    }

    /**
     * ประมาณความสูงของ generateTermsBlock ตามข้อความจริง — แทนค่าคงที่ที่ hardcode ไว้
     * ในแต่ละ generator (45 / 95) ซึ่งจะผิดทันทีถ้าผู้ใช้กำหนด quotation_terms เอง
     */
    estimateTermsHeight(termsText) {
        const paragraphs = Array.isArray(termsText) ? termsText : [termsText];
        const TERMS_WIDTH = 545;

        let height = 16; // หัวข้อ "Quotation Terms" + margin
        paragraphs.forEach((p, idx) => {
            height += this.estimateLineCount(p, TERMS_WIDTH, 7) * 9;
            if (idx < paragraphs.length - 1) height += 8;
        });

        return height + 6; // เผื่อท้ายบล็อก
    }

    /**
     * บล็อกเงื่อนไขท้ายเอกสาร รองรับทั้งย่อหน้าเดียว (string) และหลายย่อหน้า (array)
     * — Fee Estimate มีเงื่อนไข 3 ย่อหน้าแยกกัน จึงต้องเว้นระยะห่างระหว่างย่อหน้า
     * @param {string|string[]} termsText
     */
    generateTermsBlock(termsText) {
        const paragraphs = Array.isArray(termsText) ? termsText : [termsText];
        return {
            stack: [
                { text: "Quotation Terms", fontSize: 8, bold: true, color: "red", margin: [0, 6, 0, 2] },
                ...paragraphs.map((p, idx) => ({
                    text: p,
                    fontSize: 7,
                    bold: true,
                    color: "red",
                    alignment: "justify",
                    margin: idx < paragraphs.length - 1 ? [0, 0, 0, 8] : [0, 0, 0, 0],
                })),
            ],
        };
    }
    /**
 * หน้าสุดท้ายพิเศษ: Additional Information — ขึ้นหน้าใหม่เสมอ (pageBreak: "before")
 * แสดงเป็นข้อความล้วน ไม่มีตาราง/หัวข้อ/กรอบใดๆ
 * คืน null ถ้าไม่มีข้อความ (ผู้เรียกต้องเช็คก่อน push เข้า content)
 *
 * หมายเหตุ: แตก \n เป็น stack ทีละบรรทัดแทนการใส่ string ก้อนเดียว เพราะ
 * pdfMake จัดระยะบรรทัดของ \n ในก้อนเดียวไม่สม่ำเสมอเวลาข้อความยาวจนตัดข้ามหน้า
 * ส่วนบรรทัดว่าง (enter เว้นบรรทัด) แทนด้วย " " ไม่งั้น pdfMake จะยุบบรรทัดนั้นทิ้ง
 */
    generateAdditionalInfoBlock(additionalInfo) {
        const raw = (additionalInfo === null || additionalInfo === undefined) ? "" : String(additionalInfo);
        if (!raw.trim()) return null;

        const lines = raw.replace(/\r\n/g, "\n").split("\n");
        const FONT_SIZE = 9; // เท่ากับชื่อรายการใน Description / รายการ
        // ความกว้างโดยประมาณของช่องว่าง 1 ตัวในฟอนต์ Sarabun (pt) — ใช้แปลงจำนวนช่องว่าง
        // นำหน้าบรรทัดเป็นระยะย่อหน้า
        const SPACE_WIDTH = FONT_SIZE * 0.3;
        const TAB_SIZE = 4;

        // ✅ รักษาย่อหน้าตามข้อมูลเดิม: นับช่องว่าง/แท็บนำหน้าบรรทัด แล้วแปลงเป็น margin ซ้าย
        // (pdfMake ตัดช่องว่างต้นบรรทัดทิ้งเอง) ใช้ margin แทนการคงช่องว่างไว้ในข้อความ
        // เพื่อให้บรรทัดที่ยาวจนถูกตัดขึ้นบรรทัดใหม่ ยังย่อหน้าตรงกันทั้งก้อน
        // รองรับช่องว่างแบบ non-breaking ( ) และ full-width (　) ที่ติดมาจากการ copy ด้วย
        const getIndent = (line) => {
            const leading = line.match(/^[ \t 　]*/)[0];
            let count = 0;
            for (const ch of leading) {
                if (ch === "\t") count += TAB_SIZE;
                else if (ch === "　") count += 2;
                else count += 1;
            }
            return count * SPACE_WIDTH;
        };

        return {
            pageBreak: "before",
            stack: lines.map((line) => {
                const isBlank = line.trim() === "";
                return {
                    text: isBlank ? " " : line.replace(/^[ \t 　]+/, ""),
                    fontSize: FONT_SIZE,
                    alignment: "left",
                    margin: [isBlank ? 0 : getIndent(line), 0, 0, 2],
                };
            }),
        };
    }

    /**
     * ตารางรายการแบบมีกรอบ วางตำแหน่งคงที่ (absolutePosition) เพื่อให้ขอบล่างของกรอบ
     * ตรงกับจุดเริ่ม footer เป๊ะเสมอ ไม่ว่าความสูงของ header/title/client info ด้านบน
     * จะประมาณคลาดเคลื่อนไปบ้างก็ตาม — ใช้ร่วมกันทั้ง Fixed Price และ Fee Estimate
     *
     * ⚠️ ข้อจำกัด: วางด้วย absolutePosition จึงไม่ไหลข้ามหน้าอัตโนมัติ ถ้ารายการมาก
     * จนเกินพื้นที่กรอบ แถวส่วนเกินจะล้นออกนอกกรอบ/ทับ footer ได้ — เหมาะกับใบเสนอราคา
     * ทั่วไป (ไม่เกิน ~15-20 รายการ)
     *
     * @param {Object} opts
     * @param {string} opts.col1Header ข้อความหัวคอลัมน์ซ้าย
     * @param {string} opts.col2Header ข้อความหัวคอลัมน์ขวา
     * @param {Array} opts.bodyRows แถวเนื้อหา [[cellซ้าย, cellขวา], ...]
     * @param {Array<number>} opts.bodyHeights ความสูงแต่ละแถวใน bodyRows (ลำดับตรงกัน)
     * @param {Array} opts.extraRows แถวสรุปยอด/ท้ายตาราง (ต่อในตารางเดียวกัน กันเส้นซ้อน)
     * @param {Array<number>} opts.extraHeights ความสูงแต่ละแถวใน extraRows
     * @param {number} opts.startY ตำแหน่ง y เริ่มต้นของกรอบ
     * @param {number} opts.bottomY ตำแหน่ง y ของขอบล่างกรอบ (จุดเริ่ม footer)
     */
    renderBoxedTable({ col1Header, col2Header, bodyRows, bodyHeights, extraRows, extraHeights, startY, bottomY }) {
        const HEADER_ROW_HEIGHT = 26;
        const usedHeight =
            HEADER_ROW_HEIGHT +
            bodyHeights.reduce((s, h) => s + h, 0) +
            extraHeights.reduce((s, h) => s + h, 0);

        const boxHeight = Math.max(bottomY - startY, HEADER_ROW_HEIGHT);
        const fillerHeight = Math.max(boxHeight - usedHeight, 0);

        const blankRow = [
            { text: "", margin: [5, 3, 5, 3] },
            { text: "", margin: [5, 3, 5, 3] },
        ];

        const body = [
            [
                { text: col1Header, fontSize: 10, bold: true, margin: [5, 5, 5, 5] },
                { text: col2Header, fontSize: 10, bold: true, alignment: "right", margin: [5, 5, 5, 5] },
            ],
            ...bodyRows,
            blankRow,
            ...extraRows,
        ];

        const firstExtraRowIndex = body.length - extraRows.length;
        // ความสูงเป็นตัวเลขคงที่ทุกแถว (ไม่ใช้ "auto") ผลรวม = boxHeight เป๊ะ
        const heights = [HEADER_ROW_HEIGHT, ...bodyHeights, fillerHeight, ...extraHeights];

        return {
            absolutePosition: { x: 25, y: startY },
            table: {
                headerRows: 1,
                widths: [423, 120],
                body,
                heights,
            },
            layout: {
                hLineWidth: (i) => {
                    if (i === 0 || i === 1) return 1;
                    if (extraRows.length > 0 && i >= firstExtraRowIndex) return 1;
                    return 0;
                },
                vLineWidth: () => 1,
                hLineColor: () => "black",
                vLineColor: () => "black",
                paddingLeft: () => 0,
                paddingRight: () => 0,
                paddingTop: () => 0,
                paddingBottom: () => 0,
            },
        };
    }
}